import { useQuery } from '@tanstack/react-query';
import type { ProductStatus, ProductSummaryView } from '@lp/contracts';
import { Link, useNavigate } from 'react-router';
import { errorText, get } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Badge, Empty, ErrorBox, Loading, PageHeader } from '@/components/ui';

export const useProducts = () => useQuery({ queryKey: ['products'], queryFn: () => get<ProductSummaryView[]>('/platform/products') });

export function ProductStatusBadge({ status }: { status: ProductStatus }) {
  const t = useT();
  return <Badge tone={status === 'active' ? 'green' : 'gray'}>{t(status === 'active' ? 'Active' : 'Inactive')}</Badge>;
}

/** the platform's product catalog (Phase 2, docs/48): one row per product */
export function ProductsPage() {
  const t = useT();
  const nav = useNavigate();
  const list = useProducts();
  return (
    <>
      <PageHeader
        title={t('Products')}
        subtitle={t('The lottery products of the platform. A client installs a product at a version and stays on it until it is moved.')}
        actions={
          <Link to="/patterns" className="text-sm font-medium text-brand-700 hover:underline">
            {t('Result patterns')}
          </Link>
        }
      />
      <div className="rounded-lg border border-ink-200 bg-white">
        {list.isLoading ? (
          <div className="p-4">
            <Loading />
          </div>
        ) : list.error ? (
          <div className="p-4">
            <ErrorBox text={errorText(list.error)} />
          </div>
        ) : !list.data?.length ? (
          <Empty title={t('No product yet')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                  <th className="px-4 py-2">{t('Product')}</th>
                  <th className="px-4 py-2">{t('Games')}</th>
                  <th className="px-4 py-2 text-right">{t('Draws a day')}</th>
                  <th className="px-4 py-2">{t('Time zone')}</th>
                  <th className="px-4 py-2">{t('Version')}</th>
                  <th className="px-4 py-2 text-right">{t('Clients')}</th>
                  <th className="px-4 py-2">{t('State')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((p) => (
                  <tr key={p.code} onClick={() => nav(`/products/${p.code}`)} className="cursor-pointer border-b border-ink-100 last:border-0 hover:bg-ink-50" data-testid={`product-row-${p.code}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-ink-900">{p.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{p.code}</span>
                      <span className="block text-xs text-ink-500">{p.nameKm}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {p.games.map((g) => (
                        <span key={g.code} className="mr-2 inline-block">
                          <span className="font-mono">{g.code}</span> <span className="text-ink-500">×{g.draws}</span>
                          {g.rule ? null : <span className="ml-1 text-ink-400" title={t('Every post typed by hand')}>✎</span>}
                        </span>
                      ))}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.drawsPerDay ?? '—'}</td>
                    <td className="px-4 py-2.5 text-xs">{p.timezone ?? '—'}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {p.publishedVersion ? `v${p.publishedVersion}` : '—'}
                      {p.draftVersion && (
                        <span className="ml-1.5">
                          <Badge tone="blue">
                            {t('Draft')} v{p.draftVersion}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.clients}</td>
                    <td className="px-4 py-2.5">
                      <ProductStatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-ink-500">
        <span className="text-ink-400">✎</span> {t('Every post typed by hand')}
      </p>
    </>
  );
}
